import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { getUserNotifications, markNotificationRead, markAllNotificationsRead } from '../../utilities/api';
import './NotificationBell.css';

const POLL_INTERVAL_MS = 60000;

/** "just now" / "5m ago" / "3h ago" / "12 Aug 10:15" — recent alerts read better relatively. */
const formatWhen = (value) => {
    if (!value) return '';
    const date = new Date(String(value).replace(' ', 'T'));
    if (isNaN(date.getTime())) return String(value);

    const diffMinutes = Math.floor((Date.now() - date.getTime()) / 60000);
    if (diffMinutes < 1) return 'just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    if (diffMinutes < 1440) return `${Math.floor(diffMinutes / 60)}h ago`;

    const pad = (n) => String(n).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate()} ${months[date.getMonth()]} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Mirrors the server-side visibility rule, so the user can see why this list is what it is. */
const scopeNote = (role) => {
    const normalized = String(role || '').trim().toUpperCase();
    if (normalized === 'GSPOS') return 'Unread alerts across all clients';
    if (normalized === 'CEO') return 'Unread alerts for your client';
    return 'Unread alerts for your branch';
};

const NotificationBell = () => {
    const navigate = useNavigate();
    const op = useRef(null);

    const [items, setItems] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [role, setRole] = useState('');
    const [loading, setLoading] = useState(false);
    const [loadedOnce, setLoadedOnce] = useState(false);
    // Same flag as a ref, so `load` stays referentially stable and the poll timer
    // below isn't torn down and rebuilt on the first successful fetch.
    const loadedOnceRef = useRef(false);

    // The alert opened for reply, and the reply being typed.
    const [replyItem, setReplyItem] = useState(null);
    const [remarks, setRemarks] = useState('');
    const [replySaving, setReplySaving] = useState(false);

    const userId = localStorage.getItem('userid');

    const load = useCallback(
        async ({ showSpinner = false } = {}) => {
            if (!userId) return;
            if (showSpinner) setLoading(true);
            try {
                // The bell lists unread only; unread_count still comes back as the full total.
                const data = await getUserNotifications(userId, { unreadOnly: true });
                setItems(Array.isArray(data?.items) ? data.items : []);
                setUnreadCount(Number(data?.unread_count) || 0);
                setRole(data?.role || '');
                loadedOnceRef.current = true;
                setLoadedOnce(true);
            } catch {
                // A failed poll shouldn't blank an already-loaded list or nag the user.
                if (!loadedOnceRef.current) {
                    setItems([]);
                    setUnreadCount(0);
                }
            } finally {
                if (showSpinner) setLoading(false);
            }
        },
        [userId]
    );

    useEffect(() => {
        if (!userId) return undefined;
        load();
        const timer = setInterval(() => load(), POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [userId, load]);

    const openReply = (item, e) => {
        op.current.hide(e);
        setReplyItem(item);
        setRemarks('');
    };

    /** Confirms the read, with the typed reply if there is one. */
    const submitRead = async () => {
        if (!replyItem) return;
        const item = replyItem;
        setReplySaving(true);
        try {
            await markNotificationRead(userId, item.notification_id, remarks.trim());
            // Read items leave the list, so drop it locally rather than waiting for the poll.
            setItems((prev) => prev.filter((n) => n.notification_id !== item.notification_id));
            setUnreadCount((prev) => Math.max(0, prev - 1));
            setReplyItem(null);
            setRemarks('');
        } catch {
            load();
        } finally {
            setReplySaving(false);
        }
    };

    const handleMarkAll = async () => {
        if (!unreadCount) return;
        setItems([]);
        setUnreadCount(0);
        try {
            await markAllNotificationsRead(userId);
        } catch {
            load();
        }
    };

    const openPanel = (e) => {
        op.current.toggle(e);
        load({ showSpinner: !loadedOnce });
    };

    if (!userId) return null;

    const canManage = ['GSPOS', 'CEO'].includes(String(role || '').trim().toUpperCase());

    return (
        <>
            <button className="notif-bell-button" onClick={openPanel} aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`} title="Notifications">
                <i className="pi pi-bell" />
                {unreadCount > 0 && <span className="notif-bell-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>}
            </button>

            <OverlayPanel ref={op} className="notif-panel">
                <div className="notif-panel-header">
                    <span className="notif-panel-title">
                        Notifications
                        {unreadCount > 0 && <span className="notif-unread-pill">{unreadCount} unread</span>}
                    </span>
                    {unreadCount > 0 && <Button label="Mark all as read" link className="notif-markall-btn" onClick={handleMarkAll} />}
                </div>

                <div className="notif-scope-note">{scopeNote(role)}</div>

                {loading ? (
                    <div className="notif-loading">
                        <ProgressSpinner style={{ width: '38px', height: '38px' }} strokeWidth="4" />
                    </div>
                ) : items.length === 0 ? (
                    <div className="notif-empty">
                        <i className="pi pi-inbox" />
                        You're all caught up
                    </div>
                ) : (
                    <div className="notif-list">
                        {items.map((item) => (
                            <button key={item.notification_id} type="button" className="notif-item unread" onClick={(e) => openReply(item, e)} title={item.notification_details}>
                                <span className="notif-dot" />
                                <span className="notif-item-body">
                                    <p className="notif-item-text">{item.notification_details}</p>
                                    <span className="notif-item-meta">
                                        <span>{formatWhen(item.created)}</span>
                                        {item.client_name && (
                                            <>
                                                <span className="notif-meta-sep">•</span>
                                                <span>{item.client_name}</span>
                                            </>
                                        )}
                                        <span className="notif-meta-sep">•</span>
                                        <span>{item.branch_id ? item.branch_name || `Branch ${item.branch_id}` : 'All Branches'}</span>
                                    </span>
                                </span>
                            </button>
                        ))}
                    </div>
                )}

                {canManage && (
                    <div className="notif-panel-footer">
                        <Button
                            label="Manage notifications"
                            icon="pi pi-cog"
                            link
                            onClick={(e) => {
                                op.current.hide(e);
                                navigate('/notifications');
                            }}
                        />
                    </div>
                )}
            </OverlayPanel>

            {/* Reading is also the moment to reply, so the two happen in one step. */}
            <Dialog
                header={
                    <span className="notif-reply-header">
                        <i className="pi pi-bell" /> Notification
                    </span>
                }
                visible={!!replyItem}
                style={{ width: '460px' }}
                modal
                draggable={false}
                resizable={false}
                className="notif-reply-dialog"
                onHide={() => setReplyItem(null)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Close" icon="pi pi-times" text onClick={() => setReplyItem(null)} disabled={replySaving} />
                        <Button label="Mark as read" icon="pi pi-check" onClick={submitRead} loading={replySaving} />
                    </div>
                }
            >
                {replyItem && (
                    <>
                        <p className="notif-reply-text">{replyItem.notification_details}</p>
                        <div className="notif-reply-meta">
                            <span>
                                <i className="pi pi-clock" /> {formatWhen(replyItem.created)}
                            </span>
                            <span>
                                <i className="pi pi-building" /> {replyItem.branch_id ? replyItem.branch_name || `Branch ${replyItem.branch_id}` : 'All Branches'}
                            </span>
                            {replyItem.created_by && (
                                <span>
                                    <i className="pi pi-user" /> {replyItem.created_by}
                                </span>
                            )}
                        </div>

                        <label className="notif-reply-label" htmlFor="notif-remarks">
                            Your remarks <span className="notif-reply-optional">(optional)</span>
                        </label>
                        <InputTextarea
                            id="notif-remarks"
                            value={remarks}
                            onChange={(e) => setRemarks(e.target.value)}
                            rows={3}
                            autoResize
                            placeholder="Reply to whoever sent this…"
                            className="w-full"
                        />
                    </>
                )}
            </Dialog>
        </>
    );
};

export default NotificationBell;
